import { useEffect, useState } from 'react';
import {
  FolderIcon,
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  ArrowLeftIcon,
  TrashIcon,
  EyeIcon,
  CloudArrowUpIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PERMISSIONS } from '@/types';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { storageApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

interface Bucket {
  name: string;
  creationDate: string;
  objectCount: number;
  totalSize: number;
}

interface StorageObject {
  name: string;
  size: number;
  lastModified: string;
  isFolder: boolean;
  url?: string;
}

interface StorageStats {
  totalBuckets: number;
  totalObjects: number;
  totalSize: number;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const videoExts = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
  const audioExts = ['mp3', 'wav', 'ogg', 'flac', 'aac'];

  if (imageExts.includes(ext || '')) return PhotoIcon;
  if (videoExts.includes(ext || '')) return FilmIcon;
  if (audioExts.includes(ext || '')) return MusicalNoteIcon;
  return DocumentIcon;
}

function isPreviewable(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase();
  const previewableExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'mp4', 'webm', 'pdf'];
  return previewableExts.includes(ext || '');
}

function isImage(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  return imageExts.includes(ext || '');
}

function isVideo(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase();
  const videoExts = ['mp4', 'webm', 'mov'];
  return videoExts.includes(ext || '');
}

export default function StoragePage() {
  // Super admin sees the global listing (every bucket + platform stats).
  // Org-scoped users skip the listing and go straight into their own bucket.
  const { hasPermission, hasPlatformPermission } = useAuthStore();
  const isSuperAdmin = hasPermission('*');
  // Vista global de buckets: super_admin y Equipo técnico VIDA (VIDA-347). Abrir
  // un bucket (los archivos de una org) sigue siendo solo de super_admin.
  const isPlatformView = isSuperAdmin || hasPlatformPermission(PERMISSIONS.PLATFORM_SYSTEM);

  const [loading, setLoading] = useState(true);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [objects, setObjects] = useState<StorageObject[]>([]);
  const [objectsTotal, setObjectsTotal] = useState(0);
  const [objectsPage, setObjectsPage] = useState(1);
  const objectsLimit = 20;
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [loadingObjects, setLoadingObjects] = useState(false);

  // Preview modal
  const [previewObject, setPreviewObject] = useState<StorageObject | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Delete confirmation
  const [deleteObject, setDeleteObject] = useState<StorageObject | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      if (isPlatformView) {
        // Platform-wide view: every bucket + global counters.
        const [bucketsRes, statsRes] = await Promise.all([
          storageApi.listBuckets(),
          storageApi.stats(),
        ]);
        setBuckets(bucketsRes.data.buckets || []);
        setStats(statsRes.data);
      } else {
        // Org-scoped view: just the bucket of the current organization.
        const res = await storageApi.myBucket();
        const b = res.data.bucket as {
          name: string;
          objectCount: number;
          totalSize: number;
        } | null;
        if (b) {
          // Synthesize a one-bucket list and jump into it so the rest of the
          // page (objects browser) keeps working as-is.
          const synth: Bucket = {
            name: b.name,
            creationDate: new Date().toISOString(),
            objectCount: b.objectCount,
            totalSize: b.totalSize,
          };
          setBuckets([synth]);
          setSelectedBucket(b.name);
          await loadObjects(b.name, '', 1);
        }
      }
    } catch (error) {
      toast.error('Error al cargar almacenamiento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openBucket = async (bucketName: string) => {
    if (isPlatformView && !isSuperAdmin) {
      toast.error('El contenido de cada bucket solo lo ve un super_admin');
      return;
    }
    setSelectedBucket(bucketName);
    setCurrentPrefix('');
    setObjectsPage(1);
    await loadObjects(bucketName, '', 1);
  };

  const loadObjects = async (bucketName: string, prefix: string, pg?: number) => {
    setLoadingObjects(true);
    try {
      const currentPage = pg ?? objectsPage;
      const response = await storageApi.listObjects(bucketName, prefix, true, currentPage, objectsLimit);
      setObjects(response.data.objects || []);
      setObjectsTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar archivos');
      console.error(error);
    } finally {
      setLoadingObjects(false);
    }
  };

  const handleObjectsPageChange = (newPage: number) => {
    setObjectsPage(newPage);
    if (selectedBucket) {
      loadObjects(selectedBucket, currentPrefix, newPage);
    }
  };

  const navigateToFolder = async (folderName: string) => {
    const newPrefix = currentPrefix + folderName;
    setCurrentPrefix(newPrefix);
    setObjectsPage(1);
    await loadObjects(selectedBucket!, newPrefix, 1);
  };

  const goBack = async () => {
    if (!currentPrefix) {
      // Org-scoped users have no bucket listing to fall back to — keep them
      // inside their bucket. Only super_admin returns to the global listing.
      if (!isSuperAdmin) return;
      setSelectedBucket(null);
      setObjects([]);
      return;
    }

    // Go up one level
    const parts = currentPrefix.split('/').filter(Boolean);
    parts.pop();
    const newPrefix = parts.length > 0 ? parts.join('/') + '/' : '';
    setCurrentPrefix(newPrefix);
    setObjectsPage(1);
    await loadObjects(selectedBucket!, newPrefix, 1);
  };

  const handleDelete = async () => {
    if (!selectedBucket || !deleteObject) return;

    setDeleteLoading(true);
    try {
      await storageApi.deleteObject(selectedBucket, deleteObject.name);
      toast.success('Archivo eliminado');
      setShowDeleteConfirm(false);
      setDeleteObject(null);
      await loadObjects(selectedBucket, currentPrefix);
    } catch (error) {
      toast.error('Error al eliminar archivo');
      console.error(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openPreview = (obj: StorageObject) => {
    setPreviewObject(obj);
    setShowPreview(true);
  };

  if (loading) {
    return <PageLoader />;
  }

  // Bucket list view
  if (!selectedBucket) {
    return (
      <div>
        <Header
          title="Almacenamiento"
          subtitle="Gestiona los archivos multimedia (MinIO)"
        />

        <div className="p-6 space-y-6">
          {/* Stats cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-vida-100 rounded-lg">
                    <ServerIcon className="w-5 h-5 text-vida-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Buckets</p>
                    <p className="text-xl font-bold">{stats.totalBuckets}</p>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DocumentIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Archivos</p>
                    <p className="text-xl font-bold">{stats.totalObjects.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <CloudArrowUpIcon className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Almacenamiento</p>
                    <p className="text-xl font-bold">{formatBytes(stats.totalSize)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buckets list */}
          {buckets.length === 0 ? (
            <EmptyState
              icon={ServerIcon}
              title="No hay buckets"
              description="Los buckets se crean automáticamente al crear organizaciones"
            />
          ) : (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold">Buckets</h2>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Archivos</th>
                      <th>Tamaño</th>
                      <th>Creado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {buckets.map((bucket) => (
                      <tr
                        key={bucket.name}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => openBucket(bucket.name)}
                      >
                        <td>
                          <div className="flex items-center gap-3">
                            <FolderIcon className="w-5 h-5 text-amber-500" />
                            <span className="font-medium">{bucket.name}</span>
                          </div>
                        </td>
                        <td>{bucket.objectCount.toLocaleString()}</td>
                        <td>{formatBytes(bucket.totalSize)}</td>
                        <td className="text-gray-500">{formatDate(bucket.creationDate)}</td>
                        <td className="text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openBucket(bucket.name);
                            }}
                            className="p-1 text-gray-400 hover:text-vida-600"
                            title="Explorar"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Bucket contents view
  const displayName = (name: string) => {
    if (currentPrefix && name.startsWith(currentPrefix)) {
      return name.substring(currentPrefix.length);
    }
    return name;
  };

  return (
    <div>
      <Header
        title={selectedBucket}
        subtitle={currentPrefix || 'Raíz del bucket'}
        actions={
          <button onClick={goBack} className="btn-secondary">
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Volver
          </button>
        }
      />

      <div className="p-6">
        {loadingObjects ? (
          <PageLoader />
        ) : objects.length === 0 ? (
          <EmptyState
            icon={FolderIcon}
            title="Carpeta vacía"
            description="No hay archivos en esta ubicación"
          />
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Tamaño</th>
                    <th>Modificado</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {objects.map((obj) => {
                    const FileIcon = obj.isFolder ? FolderIcon : getFileIcon(obj.name);
                    const fileName = displayName(obj.name);

                    return (
                      <tr
                        key={obj.name}
                        className={obj.isFolder ? 'cursor-pointer hover:bg-gray-50' : ''}
                        onClick={() => obj.isFolder && navigateToFolder(fileName)}
                      >
                        <td>
                          <div className="flex items-center gap-3">
                            <FileIcon
                              className={`w-5 h-5 ${
                                obj.isFolder ? 'text-amber-500' : 'text-gray-400'
                              }`}
                            />
                            <span className={obj.isFolder ? 'font-medium' : ''}>
                              {fileName}
                            </span>
                          </div>
                        </td>
                        <td>{obj.isFolder ? '-' : formatBytes(obj.size)}</td>
                        <td className="text-gray-500">{formatDate(obj.lastModified)}</td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            {!obj.isFolder && isPreviewable(obj.name) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPreview(obj);
                                }}
                                className="p-1 text-gray-400 hover:text-vida-600"
                                title="Vista previa"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                            )}
                            {!obj.isFolder && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteObject(obj);
                                  setShowDeleteConfirm(true);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="Eliminar"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {objectsTotal > objectsLimit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(objectsPage - 1) * objectsLimit + 1} - {Math.min(objectsPage * objectsLimit, objectsTotal)} de {objectsTotal}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleObjectsPageChange(Math.max(1, objectsPage - 1))}
                    disabled={objectsPage === 1}
                    className="btn-secondary text-sm"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handleObjectsPageChange(objectsPage + 1)}
                    disabled={objectsPage * objectsLimit >= objectsTotal}
                    className="btn-secondary text-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview modal */}
      <Modal
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewObject(null);
        }}
        title={previewObject?.name.split('/').pop() || 'Vista previa'}
        size="lg"
      >
        {previewObject?.url && (
          <div className="flex flex-col items-center gap-4">
            {isImage(previewObject.name) && (
              <img
                src={previewObject.url}
                alt={previewObject.name}
                className="max-w-full max-h-[60vh] object-contain rounded-lg"
              />
            )}
            {isVideo(previewObject.name) && (
              <video
                src={previewObject.url}
                controls
                className="max-w-full max-h-[60vh] rounded-lg"
              />
            )}
            <div className="w-full flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-gray-500">
                <p>Tamaño: {formatBytes(previewObject.size)}</p>
                <p>Modificado: {formatDate(previewObject.lastModified)}</p>
              </div>
              <a
                href={previewObject.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Abrir en nueva pestaña
              </a>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteObject(null);
        }}
        onConfirm={handleDelete}
        title="Eliminar archivo"
        message={`¿Estás seguro de eliminar "${deleteObject?.name.split('/').pop()}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
